import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window { googleAdSquare?: any[] }
}

type GoogleAdSquareProps = {
  className?: string;
  adSlot?: string;               // e.g. "1934622448"
  adFormat?: string;             // e.g. "auto"
  fullWidth?: boolean;           // responsiveA
  showTestAds?: boolean;         // show test creatives in dev
  refreshOnPath?: boolean;       // fresh fill when pathname changes
  minHeight?: number;            // visual hint while loading
};

export default function GoogleAdSquare({
  className = "mb-6",                 // no flex here; we give the <ins> its own width
  adSlot = "1934622448",
  adFormat = "auto",
  fullWidth = true,
  showTestAds = process.env.NODE_ENV !== "production",
  refreshOnPath = true,
  minHeight = 250,
}: GoogleAdSquareProps) {
  const { pathname } = useLocation();
  const insRef = useRef<HTMLModElement | null>(null);

  // Change key on <ins> itself so SPA route changes replace the node
  const insKey = refreshOnPath ? `ins-${pathname}-${adSlot}` : `ins-${adSlot}`;

  useEffect(() => {
    const el = insRef.current;
    if (!el) return;

    // Avoid double-push in React StrictMode
    if (el.getAttribute("data-adsbygoogle-initialized") === "true") return;

    const push = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        el.setAttribute("data-adsbygoogle-initialized", "true");
      } catch {
        // benign duplicate errors can happen during dev; ignore
      }
    };

    // Only push once the loader script tag exists (kept in <head>)
    const hasLoader = !!document.querySelector('script[src*="adsbygoogle.js"]');
    if (hasLoader) {
      const t = setTimeout(push, 0); // next tick after mount
      return () => clearTimeout(t);
    }
  }, [insKey]); // run on initial mount and when path key changes

  const testAdProps = showTestAds ? { "data-adtest": "on" as const } : {};

  return (
    <div
      className={`${className} py-2.5`}
      style={{
        minWidth: 320,
        maxWidth: 400,
        width: "100%",
        margin: "0 auto",
      }}
    >
      <ins
        key={insKey}
        ref={insRef as any}
        className="adsbygoogle"
        // Give the INS its own width so it doesn't collapse inside flex containers
        style={{ display: "block", width: "100%", minHeight }}
        data-ad-client="ca-pub-1267919954757840"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidth ? "true" : "false"}
        {...testAdProps}
      />
    </div>
  );
}
